import { UserRole } from "../constants/roles";
import { api } from "./apiSlice";

export interface LoginRequest {
  email: string;
  password: string;
}


export interface UserResponse {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
}

export interface LoginResponse {
  user: UserResponse;
  accessToken: string;
  refreshToken: string;
}

export interface ApiLoginResponse {
  success: boolean;
  message: string;
  data: LoginResponse;
  errors: any[];
  statusCode: number;
}

export interface RefreshTokenRequest {
  refreshToken: string;
}

export interface SetupPasswordRequest {
  token: string;
  password: string;
  confirmPassword: string;
}

export interface PasswordSetupInfo {
  email: string;
  firstName: string;
  lastName: string;
  isValid: boolean;
  errorMessage?: string;
}

export interface ApiPasswordSetupInfoResponse {
  success: boolean;
  message: string;
  data: PasswordSetupInfo;
  errors: any[];
  statusCode: number;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ResetPasswordRequest {
  token: string;
  password: string;
  confirmPassword: string;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export interface PasswordResetInfo {
  email: string;
  firstName: string;
  lastName: string;
  isValid: boolean;
  errorMessage?: string;
}

export interface ApiPasswordResetInfoResponse {
  success: boolean;
  message: string;
  data: PasswordResetInfo;
  errors: any[];
  statusCode: number;
}

export const authApi = api.injectEndpoints({
  endpoints: (builder) => ({
    login: builder.mutation<ApiLoginResponse, LoginRequest>({
      query: (credentials) => ({
        url: "auth/login",
        method: "POST",
        body: {
          ...credentials,
          clientType: "Web",
        },
      }),
      invalidatesTags: ["User"],
    }),
    refreshToken: builder.mutation<ApiLoginResponse, RefreshTokenRequest>({
      query: (request) => ({
        url: "auth/refresh-token",
        method: "POST",
        body: request,
      }),
    }),
    revokeToken: builder.mutation<{ success: boolean; message: string; data: boolean }, RefreshTokenRequest>({
      query: (request) => ({
        url: "auth/revoke-token",
        method: "POST",
        body: request,
      }),
    }),
    logout: builder.mutation<{ success: boolean; message: string; data: boolean }, RefreshTokenRequest>({
      query: (request) => ({
        url: "auth/logout",
        method: "POST",
        body: request,
      }),
    }),
    getCurrentUser: builder.query<UserResponse, void>({
      query: () => "users/me",
      providesTags: ["User"],
    }),
    setupPassword: builder.mutation<UserResponse, SetupPasswordRequest>({
      query: (request) => ({
        url: "auth/password",
        method: "POST",
        body: request,
      }),
    }),
    getPasswordSetupInfo: builder.query<ApiPasswordSetupInfoResponse, string>({
      query: (token) => `auth/password?token=${encodeURIComponent(token)}`,
    }),
    forgotPassword: builder.mutation<{ success: boolean; message: string; data: boolean }, ForgotPasswordRequest>({
      query: (request) => ({
        url: "auth/forgot-password",
        method: "POST",
        body: request,
      }),
    }),
    resetPassword: builder.mutation<{ success: boolean; message: string; data: UserResponse }, ResetPasswordRequest>({
      query: (request) => ({
        url: "auth/reset-password",
        method: "POST",
        body: request,
      }),
    }),
    getPasswordResetInfo: builder.query<ApiPasswordResetInfoResponse, string>({
      query: (token) => `auth/reset-password?token=${encodeURIComponent(token)}`,
    }),
    changePassword: builder.mutation<{ success: boolean; message: string; data: boolean }, ChangePasswordRequest>({
      query: (request) => ({
        url: "auth/change-password",
        method: "POST",
        body: request,
      }),
    }),
  }),
});

export const {
  useLoginMutation,
  useRefreshTokenMutation,
  useRevokeTokenMutation,
  useLogoutMutation,
  useGetCurrentUserQuery,
  useSetupPasswordMutation,
  useGetPasswordSetupInfoQuery,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useGetPasswordResetInfoQuery,
  useChangePasswordMutation,
} = authApi;
